import React, { useContext, useEffect } from 'react'
import { Button, makeStyles, tokens } from '@fluentui/react-components'
import { t } from '../../i18n/i18n'
import { useForm } from 'react-hook-form'
import { DataClassification, RiskAnalysis } from '../../api/schemas/schema'
import { DataContext } from '../../context/DataContext'
import { api } from '../../helpers/apiHelper'
import { handleMessage } from '../../helpers/stateHelper'
import { ControlledDatePicker } from '../common/inputs/DatePicker'
import { ControlledTextInput, TextInput } from '../common/inputs/TextInput'
import { ModalActions } from '../common/modal/Modal'
import { IItemState } from '../../interfaces/IItemState'
import { enumValuesToOptions } from '../../helpers/enumHelper'
import { ControlledDropDown } from '../common/inputs/ControlledDropdown'
import { DraggableModal } from '../common/modal/DraggableModal'
import { Form } from '../common/form/Form'
import { Grid } from '../common/grid/Grid'
import { GridItem } from '../common/grid/GridItem'
import { ControlledAccountPicker } from '../common/inputs/ControlledAccountPicker'

const useStyles = makeStyles({
   formWrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalS,
   },
   textWrapper: {
      '> p:last-child': {
         marginTop: tokens.spacingVerticalXS,
      },
      '> p:first-child': {
         marginBottom: 0,
      },
   },
   radioToolTip: {
      top: '5px !important',
   },
   marginTop: {
      marginTop: tokens.spacingVerticalM,
   },
})
interface ModalEditRiskAssessmentInformationProps {
   analysis: RiskAnalysis
   isOpen: boolean
   closeModal: () => void
   analysisState: IItemState<RiskAnalysis>
   setAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>
}

export const ModalEditRiskAssessmentInformation = ({
   analysis,
   isOpen,
   closeModal,
   analysisState,
   setAnalysisState,
}: ModalEditRiskAssessmentInformationProps) => {
   const dataContext = useContext(DataContext)
   const { userAccount } = dataContext.state
   const { setRootState } = dataContext
   const classes = useStyles()
   const demandAssessmentForm = useForm<RiskAnalysis>({ disabled: analysisState.disabled })
   const {
      control,
      reset,
      getValues,
      trigger,
      handleSubmit,
      watch,
      setValue,
      formState: { isValid, isDirty, isSubmitting },
   } = demandAssessmentForm

   useEffect(() => {
      if (isOpen) {
         reset(analysis)
      }
   }, [isOpen, analysis, reset])

   const onSubmit = async (data: RiskAnalysis) => {
      trigger()
      if (isValid) {
         try {
            const updatedData = {
               ...data,
               modifiedById: userAccount.id,
            }

            const updatedRiskAnalysis: RiskAnalysis = (await api().updateRiskAnalysis(updatedData)).data

            setAnalysisState((prevState) => ({
               ...prevState,
               item: updatedRiskAnalysis,
            }))

            reset(updatedRiskAnalysis, { keepIsSubmitSuccessful: true, keepIsSubmitted: true })
            closeModal()
         } catch (error: any) {
            let errorMessage = t('SomethingWentWrong')
            handleMessage(setRootState, 'error', errorMessage, t('CouldNotUpdateDemandAssessment'))
         }
      }
   }

   return (
      <>
         <DraggableModal title={t('EditRiskAssesment')} open={analysis && isOpen} onClose={closeModal}>
            <Form onSubmit={handleSubmit(onSubmit)} className={classes.formWrapper}>
               <Grid>
                  <GridItem size="12/12">
                     <ControlledTextInput required control={control} name={'name'} label={t('AssessmentName')} />
                  </GridItem>
                  <GridItem size="12/12">
                     <ControlledTextInput required control={control} name={'purposeScope'} label={t('Purpose/Scope')} multiline />
                  </GridItem>
                  <GridItem size="12/12">
                     <ControlledTextInput required control={control} name={'limitation'} label={t('Limitations')} multiline />
                  </GridItem>
                  <GridItem size="12/12">
                     <TextInput
                        required
                        value={analysis.workplaces.map((workplace) => workplace.name).join(', ')}
                        label={t('Workplace')}
                        tooltipMsg={t('ValueCannotBeChanged')}
                        showTooltip
                        disabled
                     />
                  </GridItem>
                  <GridItem size="12/12">
                     <ControlledDropDown
                        required
                        control={control}
                        name={'dataClassification'}
                        label={t('InformationManagement')}
                        options={enumValuesToOptions(DataClassification)}
                     />
                  </GridItem>

                  <GridItem size="6/12">
                     <ControlledAccountPicker required label={t('RiskOwner')} control={control} name={'analysisOwner'} watch={watch} setValue={setValue} />
                  </GridItem>
                  <GridItem size="6/12">
                     <ControlledAccountPicker label={t('AnalysisLeader')} control={control} name={'analysisLeader'} watch={watch} setValue={setValue} />
                  </GridItem>

                  <GridItem size="6/12">
                     <ControlledDatePicker control={control} name={'startDate'} label={t('StartOfAssessment')} />
                  </GridItem>
                  <GridItem size="6/12">
                     <ControlledDatePicker control={control} name={'endDate'} label={t('EndOfAssessment')} getValues={getValues} compareWith={'startDate'} />
                  </GridItem>
                  <GridItem size="12/12">
                     <ControlledTextInput control={control} name={'comment'} label={t('Comment')} multiline />
                  </GridItem>
                  <GridItem size="12/12">
                     <ModalActions className={classes.marginTop}>
                        <Button onClick={closeModal} disabled={isSubmitting}>
                           {analysisState.disabled ? t('Close') : t('Cancel')}
                        </Button>

                        {!analysisState.disabled && (
                           <Button appearance="primary" type="submit" disabled={!isDirty || isSubmitting}>
                              {t('Save')}
                           </Button>
                        )}
                     </ModalActions>
                  </GridItem>
               </Grid>
            </Form>
         </DraggableModal>
      </>
   )
}
